/**
 * useInstallPrompt Hook
 *
 * Manages PWA install prompt state and provides methods to
 * trigger the install prompt or show instructions.
 */

import { useState, useEffect, useCallback } from 'react';

// Storage keys for tracking dismissal
const DISMISSED_KEY = 'pwa-install-dismissed';
const DISMISSED_UNTIL_KEY = 'pwa-install-dismissed-until';

// How long to wait before showing the prompt again after dismissal (14 days)
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000;

// Minimum time on site before showing prompt (30 seconds)
const MIN_TIME_BEFORE_PROMPT_MS = 30 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'chrome' | 'ios' | 'android' | 'unknown';

export interface UseInstallPromptResult {
  // State
  canInstall: boolean;
  isInstalled: boolean;
  platform: InstallPlatform;
  isLoading: boolean;

  // Prompt management
  shouldShowPrompt: boolean;
  dismissPrompt: (permanently?: boolean) => void;

  // Actions
  install: () => Promise<boolean>;
}

/**
 * Detect the current platform
 */
function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/.test(ua)) {
    return 'ios';
  }
  if (/Android/.test(ua)) {
    return 'android';
  }
  if (/Chrome/.test(ua) && !/Edg/.test(ua)) {
    return 'chrome';
  }
  return 'unknown';
}

/**
 * Check if the app is already installed (running as PWA)
 */
function checkIsInstalled(): boolean {
  // Check display-mode media query
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // Check iOS standalone mode
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  // Check if launched from home screen on Android
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  return false;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => checkIsInstalled());
  const [platform] = useState(() => detectPlatform());
  const [isLoading, setIsLoading] = useState(true);
  const [timeOnSite, setTimeOnSite] = useState(0);
  const [promptDismissed, setPromptDismissed] = useState(() => {
    // Check if permanently dismissed
    if (localStorage.getItem(DISMISSED_KEY) === 'true') {
      return true;
    }
    // Check if temporarily dismissed
    const dismissedUntil = localStorage.getItem(DISMISSED_UNTIL_KEY);
    if (dismissedUntil) {
      return Date.now() < parseInt(dismissedUntil, 10);
    }
    return false;
  });

  // Track time on site
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      setTimeOnSite(Date.now() - startTime);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Listen for the beforeinstallprompt event (Chrome/Android)
  useEffect(() => {
    const handler = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      console.log('[PWA] Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed');
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    // Done loading after a short delay
    const timeout = setTimeout(() => setIsLoading(false), 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timeout);
    };
  }, []);

  // Determine if we can show install prompt
  // For Chrome/Android: need the deferred prompt
  // For iOS: can always show instructions (manual install)
  const canInstall = !isInstalled && (
    deferredPrompt !== null || platform === 'ios'
  );

  // Should show the prompt banner
  const shouldShowPrompt =
    canInstall &&
    !isLoading &&
    !promptDismissed &&
    timeOnSite >= MIN_TIME_BEFORE_PROMPT_MS;

  // Dismiss the prompt
  const dismissPrompt = useCallback((permanently = false) => {
    if (permanently) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } else {
      localStorage.setItem(
        DISMISSED_UNTIL_KEY,
        (Date.now() + DISMISS_DURATION_MS).toString()
      );
    }
    setPromptDismissed(true);
  }, []);

  // Trigger the install
  const install = useCallback(async (): Promise<boolean> => {
    if (platform === 'ios') {
      // Can't programmatically install on iOS, just dismiss the prompt
      // The banner will show instructions
      return false;
    }

    if (!deferredPrompt) {
      console.warn('[PWA] No install prompt available');
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] User choice:', outcome);

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setDeferredPrompt(null);
        return true;
      }
    } catch (err) {
      console.error('[PWA] Install failed:', err);
    }

    return false;
  }, [deferredPrompt, platform]);

  return {
    canInstall,
    isInstalled,
    platform,
    isLoading,
    shouldShowPrompt,
    dismissPrompt,
    install,
  };
}

